import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import SEO from '../components/SEO';
import { ArrowRight, Briefcase, Code2, Wrench, GraduationCap, ShoppingBag, Stethoscope, CheckCircle2 } from 'lucide-react';

const INDUSTRIES = [
  {
    icon: Briefcase, slug: 'agenturen', title: 'Agenturen',
    problem: 'Kaltakquise frisst Zeit, die in Kundenprojekte fließen sollte. Klassische Adresslisten liefern nur lauwarme Kontakte.',
    solution: 'LaizyLeads identifiziert gezielt passende Unternehmen mit konkretem Bedarf an Marketing-, Performance- oder Branding-Leistung – inklusive fertigem Aufhänger für die Erstansprache.',
    use: 'B2B Neukunden automatisch finden für Performance-, Branding-, SEO- und Social-Media-Agenturen.',
  },
  {
    icon: Code2, slug: 'saas', title: 'SaaS & Software',
    problem: 'Outbound-Teams brauchen schnell skalierbare Listen mit dem richtigen ICP – aber Standard-Datenbanken passen selten exakt.',
    solution: 'Beschreiben Sie Ihren ICP per Sprache oder Text. Die KI sucht datenbasiert nach Unternehmen mit passender Größe, Branche und Tech-Stack-Indikatoren.',
    use: 'Vertrieb Leads B2B für SaaS, Plattformen, IT-Lösungen, Cloud-Services und API-Anbieter.',
  },
  {
    icon: Wrench, slug: 'dienstleister', title: 'B2B-Dienstleister',
    problem: 'Berater, Consulting, IT- und Engineering-Dienstleister verlieren Zeit mit unqualifizierten Anfragen.',
    solution: 'Hochwertige Leads mit Begründung, Standort und Ansprechpartner – sodass Ihr Vertrieb sich auf die richtigen Gespräche konzentriert.',
    use: 'Kundenakquise B2B für Beratungen, Engineering, Steuerberatung, Personalvermittlung, Facility Management.',
  },
  {
    icon: ShoppingBag, slug: 'industrie-handel', title: 'Industrie & Großhandel',
    problem: 'Vertriebsaußendienst arbeitet noch immer mit Excel-Listen aus Messen und Branchenbüchern.',
    solution: 'Aktuelle, geprüfte Datenbasis statt veralteter Adressen – inkl. Standort-Verifizierung über Impressum-Abgleich.',
    use: 'Mehr Umsatz durch bessere Leads im Maschinenbau, technischen Großhandel, Zulieferern und Industriedienstleistern.',
  },
  {
    icon: GraduationCap, slug: 'bildung', title: 'Bildung & Weiterbildung',
    problem: 'Akademien und Trainer suchen Firmenkunden für Inhouse-Trainings – aber wo anfangen?',
    solution: 'Wir liefern Unternehmen mit passender Größe, Branche und Region für Ihre Bildungsangebote – als datenbasierte Leadgenerierung.',
    use: 'Gezielt passende Unternehmen finden für Inhouse-Trainings, Weiterbildung, Coaching-Programme.',
  },
  {
    icon: Stethoscope, slug: 'gesundheit', title: 'Gesundheits- & Medizintechnik',
    problem: 'Vertrieb in Praxen, Kliniken und MVZ ist regulatorisch anspruchsvoll und stark regional.',
    solution: 'Regionale, KI-bewertete Leads – mit Bundesland-Filter und PLZ-Verifizierung – für einen DSGVO-konformen Vertrieb.',
    use: 'Qualifizierte Leads für Medizintechnik, Praxis-Software, Healthcare-SaaS und Klinik-Dienstleister.',
  },
];

export default function BranchenPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="branchen-page">
      <SEO
        title="Branchen & Use Cases – B2B Leadgenerierung für jede Branche | LaizyLeads"
        description="Vertrieb Leads B2B für Agenturen, SaaS, Dienstleister, Industrie & mehr. So hilft der LaizyLeads Leadgenerator, gezielt passende Unternehmen zu finden und qualifizierte Leads zu generieren."
        path="/branchen"
        keywords="Vertrieb Leads B2B, Kundenakquise B2B, B2B Neukunden automatisch finden, gezielt passende Unternehmen finden, Leadgenerator Agenturen, Leadgenerator SaaS"
      />
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-gray-50" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 border-emerald-200 px-3.5 py-1.5 text-sm font-medium">
            <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Branchen
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            Für wen LaizyLeads<span className="block text-emerald-600">Neukunden gewinnt.</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
            Ob Agentur, SaaS-Anbieter oder Industriedienstleister – wer im B2B Leads generieren will, braucht eine Lösung, die die jeweilige Branche versteht. LaizyLeads übersetzt Ihren Vertriebskontext in eine präzise, datenbasierte Leadgenerierung.
          </p>
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white h-12 px-8" onClick={() => navigate('/register')} data-testid="branchen-register-btn">
                Jetzt loslegen <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 h-12 px-8" onClick={() => navigate('/leistungen')}>
                Wie es funktioniert
              </Button>
            </div>
            <p className="text-xs text-gray-500 italic">*monatlich kündbar</p>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <article key={i} className="p-7 rounded-2xl border border-gray-200 bg-gray-50 hover:border-emerald-200 hover:bg-white hover:shadow-md transition-all" data-testid={`branche-${ind.slug}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h2 className="text-xl font-semibold">{ind.title}</h2>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Problem</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{ind.problem}</p>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Lösung</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{ind.solution}</p>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Use Case</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{ind.use}</span>
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">Sie sind in einer anderen Branche?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl">
            Unsere KI ist branchenagnostisch. Wenn Sie B2B-Kunden haben, kann LaizyLeads passende Unternehmen finden – auch in Nischenmärkten. Vereinbaren Sie ein kurzes Gespräch oder lesen Sie weiter:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link to="/leistungen" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Leistungen</h3>
              <p className="text-sm text-gray-500">Wie unser Leadgenerator hochwertige Leads liefert.</p>
            </Link>
            <Link to="/case-studies" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Case Studies</h3>
              <p className="text-sm text-gray-500">Echte Ergebnisse: bessere Zielkunden, mehr Umsatz.</p>
            </Link>
            <Link to="/kontakt" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Kostenlose Beratung</h3>
              <p className="text-sm text-gray-500">Wir prüfen kostenlos, ob LaizyLeads zu Ihrer Branche passt.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-700" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">B2B Neukunden automatisch finden</h2>
          <p className="text-emerald-100 text-base md:text-lg mb-8 max-w-xl mx-auto">
            Lassen Sie LaizyLeads Ihre nächsten qualifizierten B2B-Leads liefern – passend zu Ihrer Branche, Ihrem Standort und Ihrem Angebot.
          </p>
          <Button size="lg" className="bg-white hover:bg-gray-100 text-emerald-700 h-12 px-8 text-base shadow-xl font-semibold" onClick={() => navigate('/register')}>
            Jetzt registrieren <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
